/**
 * Custom React Flow node components — one per NodeType.
 *
 * Each wraps NodeShell with type-specific handles and subtitle.
 * NODE_TYPES map is defined at module level (CRITICAL for React Flow
 * performance -- prevents re-mounting all nodes on every render).
 */

import { type NodeProps, Handle, Position } from '@xyflow/react';
import { NodeShell, type SourceHandle, type TargetHandle } from './node-shell';
import { NodeInspectBadge, type NodeInspectData } from './node-inspect-badge';
import { cn } from '@/lib/utils';
import type { NodeType } from '@/lib/mock-automate-data';

// ── Helpers to extract subtitles from node data.config ───────────────────

function triggerSubtitle(config: Record<string, unknown>): string | undefined {
  const tt = config.triggerType as string | undefined;
  if (tt === 'scheduled' || tt === 'cron') {
    const cron = (config.cron ?? config.cronExpression) as string | undefined;
    return cron ? `Cron: ${cron}` : 'Scheduled';
  }
  if (tt === 'event') {
    const ev = config.eventType as string | undefined;
    return ev ? `Event: ${ev}` : 'Event-triggered';
  }
  if (tt === 'webhook') return 'Webhook trigger';
  if (tt === 'manual') return 'Manual trigger';
  return tt ?? undefined;
}

function agentSubtitle(config: Record<string, unknown>): string | undefined {
  return (config.agentName ?? config.agentId) as string | undefined;
}

function toolSubtitle(config: Record<string, unknown>): string | undefined {
  return (config.tool ?? config.toolName) as string | undefined;
}

function conditionSubtitle(config: Record<string, unknown>): string | undefined {
  const field = config.field as string | undefined;
  const op = config.operator as string | undefined;
  const val = config.value as string | undefined;
  if (field && op) {
    const opLabel: Record<string, string> = { eq: '=', gt: '>', lt: '<', gte: '>=', lte: '<=', contains: 'contains', regex: '~', exists: 'exists' };
    return `if ${field} ${opLabel[op] ?? op} ${val ?? ''}`.trim();
  }
  return undefined;
}

function aiRouterSubtitle(config: Record<string, unknown>): string | undefined {
  const cats = config.categories as string[] | undefined;
  if (cats && cats.length > 0) return `Classify intent -> ${cats.length} categories`;
  return 'AI-powered routing';
}

function approvalSubtitle(config: Record<string, unknown>): string | undefined {
  const level = config.level as string | undefined;
  const labels: Record<string, string> = { single: 'Single approval', supervisor: 'Supervisor approval', two_person: '2-person rule' };
  return level ? (labels[level] ?? `${level} approval`) : 'Requires approval';
}

function loopSubtitle(config: Record<string, unknown>): string | undefined {
  const loopType = config.type as string | undefined;
  const collection = config.collection as string | undefined;
  if (loopType === 'iterate' && collection) return `For each ${collection}`;
  if (loopType === 'repeat') return `Repeat ${config.count ?? 'N'} times`;
  if (loopType === 'while') return `While ${config.condition ?? '...'}`;
  return collection ? `For each ${collection}` : 'Loop';
}

function delaySubtitle(config: Record<string, unknown>): string | undefined {
  const dur = config.duration as number | undefined;
  const unit = (config.unit as string) ?? 'minutes';
  if (dur) return `Wait ${dur} ${unit}`;
  return undefined;
}

function webhookSubtitle(config: Record<string, unknown>): string | undefined {
  const method = (config.method as string) ?? 'POST';
  const url = config.url as string | undefined;
  if (url) return `${method} ${url.length > 28 ? url.slice(0, 28) + '...' : url}`;
  return `${method} request`;
}

function notificationSubtitle(config: Record<string, unknown>): string | undefined {
  const channel = config.channel as string | undefined;
  const recipients = config.recipients as string[] | undefined;
  const parts: string[] = [];
  if (channel) parts.push(channel.charAt(0).toUpperCase() + channel.slice(1));
  if (recipients && recipients.length > 0) parts.push(`to ${recipients[0]}${recipients.length > 1 ? ` +${recipients.length - 1}` : ''}`);
  return parts.length > 0 ? parts.join(' ') : undefined;
}

// ── Node data interface ──────────────────────────────────────────────────

interface WorkflowNodeData {
  label: string;
  config: Record<string, unknown>;
  status?: 'idle' | 'running' | 'success' | 'error';
  /** Inspect data shown as badge after test run */
  inspectData?: NodeInspectData;
  /** Stagger index for badge pop animation */
  inspectStaggerIndex?: number;
  /** Current step in step-through mode */
  isCurrentStep?: boolean;
  /** Next step in step-through mode */
  isNextStep?: boolean;
  [key: string]: unknown;
}

// ── Trigger Node ─────────────────────────────────────────────────────────
// 1 source (bottom), no target
// Enhanced: pulse ring when status is 'running' (workflow active)

export function TriggerNode({ data, selected }: NodeProps) {
  const d = data as unknown as WorkflowNodeData;
  const isActive = d.status === 'running';

  return (
    <div className={cn(
      'relative min-w-[200px] max-w-[220px] rounded-lg border-l-4 border-l-amber-500 bg-background shadow-sm border border-border',
      selected && 'ring-2 ring-primary ring-offset-1 ring-offset-background',
      d.isCurrentStep && 'ring-2 ring-blue-500 ring-offset-2 ring-offset-background',
      d.isNextStep && 'border-dashed border-2 border-blue-400/60',
    )}
      style={d.isCurrentStep ? { animation: 'pulse-ring 1.5s ease-in-out infinite' } : undefined}
    >
      {/* Inspect badge (after test run) */}
      {d.inspectData && (
        <NodeInspectBadge data={d.inspectData} staggerIndex={d.inspectStaggerIndex ?? 0} />
      )}

      {/* Pulse ring for active trigger */}
      {isActive && (
        <div className="absolute -inset-2 rounded-xl border-2 border-amber-400/50 animate-ping pointer-events-none" />
      )}
      {isActive && (
        <div className="absolute -inset-1.5 rounded-lg border border-amber-400/30 animate-pulse pointer-events-none" />
      )}

      {/* Body */}
      <div className="flex items-center gap-2.5 px-3 py-2.5">
        <div className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-md bg-amber-500/10">
          <svg className="h-4 w-4 text-amber-600 dark:text-amber-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" /></svg>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-semibold leading-tight truncate">{d.label}</span>
            <span className={cn(
              'flex-shrink-0 w-1.5 h-1.5 rounded-full',
              d.status === 'running' ? 'bg-blue-500 animate-pulse'
                : d.status === 'success' ? 'bg-emerald-500'
                : d.status === 'error' ? 'bg-red-500'
                : 'bg-zinc-400 dark:bg-zinc-500',
            )} />
          </div>
          {triggerSubtitle(d.config) && (
            <p className="text-[10px] text-muted-foreground leading-tight mt-0.5 truncate">{triggerSubtitle(d.config)}</p>
          )}
        </div>
      </div>

      {/* Source handle (bottom) */}
      <Handle
        id="output"
        type="source"
        position={Position.Bottom}
        className="!w-2.5 !h-2.5 !bg-zinc-400 dark:!bg-zinc-500 !border-2 !border-background"
      />
    </div>
  );
}

// ── Agent Node ───────────────────────────────────────────────────────────
// 1 target (top), 1 source (bottom)

export function AgentNode({ data, selected }: NodeProps) {
  const d = data as unknown as WorkflowNodeData;
  const config = d.config ?? {};
  const tools = (config.tools as string[]) ?? [];
  const skills = (config.skills as string[]) ?? [];
  const knowledgeFolders = (config.knowledgeFolders as string[]) ?? [];
  const knowledgeDocs = (config.knowledgeDocs as string[]) ?? [];
  const modelId = (config.model as string) ?? 'default';
  const modelLabel = modelId === 'fast' ? 'Fast 3B' : modelId === 'code' ? 'Code 32B' : modelId === 'large' ? 'Large 128K' : modelId === 'max' ? 'Max 1M' : '72B AWQ';

  return (
    <div
      className={cn(
        'relative min-w-[240px] max-w-[280px] rounded-lg border-l-4 border-l-purple-500 bg-background shadow-md border',
        selected ? 'border-primary ring-2 ring-primary/30' : 'border-border',
        d.isCurrentStep && 'ring-2 ring-blue-500 ring-offset-2 ring-offset-background',
        d.isNextStep && 'border-dashed border-2 border-blue-400/60',
      )}
      style={d.isCurrentStep ? { animation: 'pulse-ring 1.5s ease-in-out infinite' } : undefined}
    >
      {/* Inspect badge (after test run) */}
      {d.inspectData && (
        <NodeInspectBadge data={d.inspectData} staggerIndex={d.inspectStaggerIndex ?? 0} />
      )}

      {/* Target handle */}
      <Handle type="target" position={Position.Top} id="input" className="!w-2.5 !h-2.5 !bg-zinc-400 dark:!bg-zinc-500 !border-2 !border-background" />

      {/* Header */}
      <div className="px-3 pt-2.5 pb-1.5 flex items-center gap-2">
        <div className="w-7 h-7 rounded-md bg-purple-500/10 flex items-center justify-center shrink-0">
          <span className="text-purple-600 dark:text-purple-400 text-xs">🤖</span>
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold truncate">{d.label}</p>
          <p className="text-[9px] text-muted-foreground truncate">{agentSubtitle(config) ?? 'Inline agent'}</p>
        </div>
        {d.status && d.status !== 'idle' && (
          <span className={`h-2 w-2 rounded-full shrink-0 ${d.status === 'running' ? 'bg-blue-500 animate-pulse' : d.status === 'success' ? 'bg-emerald-500' : d.status === 'error' ? 'bg-red-500' : 'bg-zinc-400'}`} />
        )}
      </div>

      {/* Stats row */}
      <div className="px-3 pb-2 flex items-center gap-2 flex-wrap">
        <span className="inline-flex items-center gap-0.5 text-[8px] bg-muted rounded px-1.5 py-0.5">
          <span className="text-muted-foreground">Model:</span> <span className="font-medium">{modelLabel}</span>
        </span>
        {tools.length > 0 && (
          <span className="inline-flex items-center gap-0.5 text-[8px] bg-blue-500/10 text-blue-600 dark:text-blue-400 rounded px-1.5 py-0.5">
            🔧 {tools.length} tools
          </span>
        )}
        {skills.length > 0 && (
          <span className="inline-flex items-center gap-0.5 text-[8px] bg-purple-500/10 text-purple-600 dark:text-purple-400 rounded px-1.5 py-0.5">
            📚 {skills.length} skills
          </span>
        )}
        {(knowledgeFolders.length > 0 || knowledgeDocs.length > 0) && (
          <span className="inline-flex items-center gap-0.5 text-[8px] bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 rounded px-1.5 py-0.5">
            📄 {knowledgeFolders.includes('folder-all') ? 'All docs' : `${knowledgeFolders.length + knowledgeDocs.length} sources`}
          </span>
        )}
      </div>

      {/* Source handle */}
      <Handle type="source" position={Position.Bottom} id="output" className="!w-2.5 !h-2.5 !bg-zinc-400 dark:!bg-zinc-500 !border-2 !border-background" />
    </div>
  );
}

// ── MCP Tool Node ────────────────────────────────────────────────────────
// 1 target (top), 1 source (bottom)

export function McpToolNode({ data, selected }: NodeProps) {
  const d = data as unknown as WorkflowNodeData;
  const targets: TargetHandle[] = [{ id: 'input', position: 'top' }];
  const sources: SourceHandle[] = [{ id: 'output', position: 'bottom' }];

  return (
    <NodeShell
      type="mcp_tool"
      label={d.label}
      subtitle={toolSubtitle(d.config)}
      status={d.status}
      selected={selected}
      inspectData={d.inspectData}
      inspectStaggerIndex={d.inspectStaggerIndex}
      isCurrentStep={d.isCurrentStep}
      isNextStep={d.isNextStep}
      targetHandles={targets}
      sourceHandles={sources}
    />
  );
}

// ── Condition Node ───────────────────────────────────────────────────────
// 1 target (top), 2 sources (right: "true" and "false")
// Enhanced: colored "IF" banner, colored branch handles

export function ConditionNode({ data, selected }: NodeProps) {
  const d = data as unknown as WorkflowNodeData;

  return (
    <div className={cn(
      'relative min-w-[200px] max-w-[220px] rounded-lg border-2 border-emerald-500/50 bg-background shadow-sm',
      selected && 'ring-2 ring-primary ring-offset-1 ring-offset-background',
    )}>
      {/* Target handle */}
      <Handle id="input" type="target" position={Position.Top}
        className="!w-2.5 !h-2.5 !bg-zinc-400 dark:!bg-zinc-500 !border-2 !border-background" />

      {/* IF banner */}
      <div className="bg-emerald-500 text-white text-[9px] font-bold uppercase tracking-wider text-center py-0.5 rounded-t-[5px]">
        IF
      </div>

      {/* Body */}
      <div className="flex items-center gap-2.5 px-3 py-2">
        <div className="flex-shrink-0 flex items-center justify-center w-7 h-7 rounded-md bg-emerald-500/10">
          <svg className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="6" y1="3" x2="6" y2="15" /><circle cx="18" cy="6" r="3" /><circle cx="6" cy="18" r="3" /><path d="M18 9a9 9 0 0 1-9 9" /></svg>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-semibold leading-tight truncate">{d.label}</span>
            <span className={cn(
              'flex-shrink-0 w-1.5 h-1.5 rounded-full',
              d.status === 'running' ? 'bg-blue-500 animate-pulse'
                : d.status === 'success' ? 'bg-emerald-500'
                : d.status === 'error' ? 'bg-red-500'
                : 'bg-zinc-400 dark:bg-zinc-500',
            )} />
          </div>
          {conditionSubtitle(d.config) && (
            <p className="text-[10px] text-muted-foreground leading-tight mt-0.5 truncate">{conditionSubtitle(d.config)}</p>
          )}
        </div>
      </div>

      {/* Right-side source handles with branch labels */}
      <Handle id="true" type="source" position={Position.Right}
        style={{ top: '40%' }}
        className="!w-2.5 !h-2.5 !bg-emerald-500 !border-2 !border-background"
      >
        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[8px] font-semibold text-emerald-600 dark:text-emerald-400 whitespace-nowrap pointer-events-none">true</span>
      </Handle>
      <Handle id="false" type="source" position={Position.Right}
        style={{ top: '70%' }}
        className="!w-2.5 !h-2.5 !bg-red-400 !border-2 !border-background"
      >
        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[8px] font-semibold text-red-500 dark:text-red-400 whitespace-nowrap pointer-events-none">false</span>
      </Handle>
    </div>
  );
}

// ── AI Router Node ───────────────────────────────────────────────────────
// 1 target (top), N sources (right, one per category)

export function AiRouterNode({ data, selected }: NodeProps) {
  const d = data as unknown as WorkflowNodeData;
  const cats = (d.config.categories as string[]) ?? ['category_1', 'category_2'];
  const targets: TargetHandle[] = [{ id: 'input', position: 'top' }];
  const sources: SourceHandle[] = cats.map((cat, i) => ({
    id: `cat_${i}`,
    position: 'right' as const,
    label: cat,
  }));

  return (
    <NodeShell
      type="ai_router"
      label={d.label}
      subtitle={aiRouterSubtitle(d.config)}
      status={d.status}
      selected={selected}
      inspectData={d.inspectData}
      inspectStaggerIndex={d.inspectStaggerIndex}
      isCurrentStep={d.isCurrentStep}
      isNextStep={d.isNextStep}
      targetHandles={targets}
      sourceHandles={sources}
    />
  );
}

// ── Approval Node ────────────────────────────────────────────────────────
// 1 target (top), 2 sources (right: "approved" and "rejected")
// Enhanced: amber border, pulsing dashed border + pause icon when "running" (waiting)

export function ApprovalNode({ data, selected }: NodeProps) {
  const d = data as unknown as WorkflowNodeData;
  const isWaiting = d.status === 'running';

  return (
    <div className={cn(
      'relative min-w-[200px] max-w-[220px] rounded-lg bg-background shadow-sm',
      isWaiting
        ? 'border-2 border-dashed border-amber-500 animate-pulse'
        : 'border-2 border-amber-500/40',
      selected && 'ring-2 ring-primary ring-offset-1 ring-offset-background',
    )}>
      {/* Target handle */}
      <Handle id="input" type="target" position={Position.Top}
        className="!w-2.5 !h-2.5 !bg-zinc-400 dark:!bg-zinc-500 !border-2 !border-background" />

      {/* Body */}
      <div className="flex items-center gap-2.5 px-3 py-2.5">
        <div className={cn(
          'flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-md',
          isWaiting ? 'bg-amber-500/20' : 'bg-muted/60',
        )}>
          {isWaiting ? (
            /* Pause icon (two vertical bars) when waiting for approval */
            <svg className="h-4 w-4 text-amber-600 dark:text-amber-400" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16" rx="1" /><rect x="14" y="4" width="4" height="16" rx="1" /></svg>
          ) : (
            <svg className="h-4 w-4 text-red-600 dark:text-red-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><polyline points="9 12 12 15 16 10" /></svg>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-semibold leading-tight truncate">{d.label}</span>
            {isWaiting && (
              <span className="text-[8px] font-medium text-amber-600 dark:text-amber-400 bg-amber-500/10 rounded px-1 py-0.5">WAITING</span>
            )}
            {!isWaiting && (
              <span className={cn(
                'flex-shrink-0 w-1.5 h-1.5 rounded-full',
                d.status === 'success' ? 'bg-emerald-500'
                  : d.status === 'error' ? 'bg-red-500'
                  : 'bg-zinc-400 dark:bg-zinc-500',
              )} />
            )}
          </div>
          {approvalSubtitle(d.config) && (
            <p className="text-[10px] text-muted-foreground leading-tight mt-0.5 truncate">{approvalSubtitle(d.config)}</p>
          )}
        </div>
      </div>

      {/* Right-side source handles */}
      <Handle id="approved" type="source" position={Position.Right}
        style={{ top: '35%' }}
        className="!w-2.5 !h-2.5 !bg-emerald-500 !border-2 !border-background"
      >
        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[8px] font-semibold text-emerald-600 dark:text-emerald-400 whitespace-nowrap pointer-events-none">approved</span>
      </Handle>
      <Handle id="rejected" type="source" position={Position.Right}
        style={{ top: '65%' }}
        className="!w-2.5 !h-2.5 !bg-red-400 !border-2 !border-background"
      >
        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[8px] font-semibold text-red-500 dark:text-red-400 whitespace-nowrap pointer-events-none">rejected</span>
      </Handle>
    </div>
  );
}

// ── Loop Node ────────────────────────────────────────────────────────────
// 1 target (top), 2 sources (bottom: "body", right: "done")

export function LoopNode({ data, selected }: NodeProps) {
  const d = data as unknown as WorkflowNodeData;
  const targets: TargetHandle[] = [{ id: 'input', position: 'top' }];
  const sources: SourceHandle[] = [
    { id: 'body', position: 'bottom', label: 'body' },
    { id: 'done', position: 'right', label: 'done' },
  ];

  return (
    <NodeShell
      type="loop"
      label={d.label}
      subtitle={loopSubtitle(d.config)}
      status={d.status}
      selected={selected}
      inspectData={d.inspectData}
      inspectStaggerIndex={d.inspectStaggerIndex}
      isCurrentStep={d.isCurrentStep}
      isNextStep={d.isNextStep}
      targetHandles={targets}
      sourceHandles={sources}
    />
  );
}

// ── Delay Node ───────────────────────────────────────────────────────────
// 1 target (top), 1 source (bottom)

export function DelayNode({ data, selected }: NodeProps) {
  const d = data as unknown as WorkflowNodeData;
  const targets: TargetHandle[] = [{ id: 'input', position: 'top' }];
  const sources: SourceHandle[] = [{ id: 'output', position: 'bottom' }];

  return (
    <NodeShell
      type="delay"
      label={d.label}
      subtitle={delaySubtitle(d.config)}
      status={d.status}
      selected={selected}
      inspectData={d.inspectData}
      inspectStaggerIndex={d.inspectStaggerIndex}
      isCurrentStep={d.isCurrentStep}
      isNextStep={d.isNextStep}
      targetHandles={targets}
      sourceHandles={sources}
    />
  );
}

// ── Webhook Node ─────────────────────────────────────────────────────────
// 1 target (top), 1 source (bottom)

export function WebhookNode({ data, selected }: NodeProps) {
  const d = data as unknown as WorkflowNodeData;
  const targets: TargetHandle[] = [{ id: 'input', position: 'top' }];
  const sources: SourceHandle[] = [{ id: 'output', position: 'bottom' }];

  return (
    <NodeShell
      type="webhook"
      label={d.label}
      subtitle={webhookSubtitle(d.config)}
      status={d.status}
      selected={selected}
      inspectData={d.inspectData}
      inspectStaggerIndex={d.inspectStaggerIndex}
      isCurrentStep={d.isCurrentStep}
      isNextStep={d.isNextStep}
      targetHandles={targets}
      sourceHandles={sources}
    />
  );
}

// ── Notification Node ────────────────────────────────────────────────────
// 1 target (top), 1 source (bottom)

export function NotificationNode({ data, selected }: NodeProps) {
  const d = data as unknown as WorkflowNodeData;
  const targets: TargetHandle[] = [{ id: 'input', position: 'top' }];
  const sources: SourceHandle[] = [{ id: 'output', position: 'bottom' }];

  return (
    <NodeShell
      type="notification"
      label={d.label}
      subtitle={notificationSubtitle(d.config)}
      status={d.status}
      selected={selected}
      inspectData={d.inspectData}
      inspectStaggerIndex={d.inspectStaggerIndex}
      isCurrentStep={d.isCurrentStep}
      isNextStep={d.isNextStep}
      targetHandles={targets}
      sourceHandles={sources}
    />
  );
}

// ── LLM Generate Node ────────────────────────────────────────────────────
// Direct LLM call with prompt template. 1 target, 1 source.

export function LlmGenerateNode({ data, selected }: NodeProps) {
  const d = data as unknown as WorkflowNodeData;
  const model = (d.config?.model as string) ?? 'default';
  const subtitle = model === 'fast' ? 'Fast 3B' : model === 'code' ? 'Code 32B' : 'Default 72B';
  return (
    <NodeShell type="llm_generate" label={d.label} subtitle={`LLM: ${subtitle}`} status={d.status} selected={selected}
      inspectData={d.inspectData} inspectStaggerIndex={d.inspectStaggerIndex} isCurrentStep={d.isCurrentStep} isNextStep={d.isNextStep}
      targetHandles={[{ id: 'input', position: 'top' }]} sourceHandles={[{ id: 'output', position: 'bottom' }]} />
  );
}

// ── RAG Search Node ──────────────────────────────────────────────────────
// Knowledge base search. 1 target, 1 source.

export function RagSearchNode({ data, selected }: NodeProps) {
  const d = data as unknown as WorkflowNodeData;
  const topK = (d.config?.topK as number) ?? 5;
  return (
    <NodeShell type="rag_search" label={d.label} subtitle={`Top ${topK} chunks`} status={d.status} selected={selected}
      inspectData={d.inspectData} inspectStaggerIndex={d.inspectStaggerIndex} isCurrentStep={d.isCurrentStep} isNextStep={d.isNextStep}
      targetHandles={[{ id: 'input', position: 'top' }]} sourceHandles={[{ id: 'output', position: 'bottom' }]} />
  );
}

// ── Set Variable Node ────────────────────────────────────────────────────
// Transform/map/set data. 1 target, 1 source.

export function SetVariableNode({ data, selected }: NodeProps) {
  const d = data as unknown as WorkflowNodeData;
  const varName = (d.config?.variableName as string) ?? '';
  return (
    <NodeShell type="set_variable" label={d.label} subtitle={varName ? `Set: ${varName}` : 'Transform data'} status={d.status} selected={selected}
      inspectData={d.inspectData} inspectStaggerIndex={d.inspectStaggerIndex} isCurrentStep={d.isCurrentStep} isNextStep={d.isNextStep}
      targetHandles={[{ id: 'input', position: 'top' }]} sourceHandles={[{ id: 'output', position: 'bottom' }]} />
  );
}

// ── Merge Node ───────────────────────────────────────────────────────────
// Combine outputs from parallel branches. 2+ targets (top), 1 source (bottom).

export function MergeNode({ data, selected }: NodeProps) {
  const d = data as unknown as WorkflowNodeData;
  const strategy = (d.config?.strategy as string) ?? 'append';
  return (
    <NodeShell type="merge" label={d.label} subtitle={`Strategy: ${strategy}`} status={d.status} selected={selected}
      inspectData={d.inspectData} inspectStaggerIndex={d.inspectStaggerIndex} isCurrentStep={d.isCurrentStep} isNextStep={d.isNextStep}
      targetHandles={[{ id: 'input-1', position: 'top' }, { id: 'input-2', position: 'left' }]}
      sourceHandles={[{ id: 'output', position: 'bottom' }]} />
  );
}

// ── Code Node ────────────────────────────────────────────────────────────
// Custom JavaScript/Python expression. 1 target, 1 source.

export function CodeNode({ data, selected }: NodeProps) {
  const d = data as unknown as WorkflowNodeData;
  const lang = (d.config?.language as string) ?? 'javascript';
  return (
    <NodeShell type="code" label={d.label} subtitle={lang === 'python' ? 'Python' : 'JavaScript'} status={d.status} selected={selected}
      inspectData={d.inspectData} inspectStaggerIndex={d.inspectStaggerIndex} isCurrentStep={d.isCurrentStep} isNextStep={d.isNextStep}
      targetHandles={[{ id: 'input', position: 'top' }]} sourceHandles={[{ id: 'output', position: 'bottom' }]} />
  );
}

// ── Sub-Workflow Node ────────────────────────────────────────────────────
// Invoke another workflow. 1 target, 1 source.

export function SubWorkflowNode({ data, selected }: NodeProps) {
  const d = data as unknown as WorkflowNodeData;
  const wfName = (d.config?.workflowName as string) ?? '';
  return (
    <NodeShell type="sub_workflow" label={d.label} subtitle={wfName ? `Run: ${wfName}` : 'Select workflow'} status={d.status} selected={selected}
      inspectData={d.inspectData} inspectStaggerIndex={d.inspectStaggerIndex} isCurrentStep={d.isCurrentStep} isNextStep={d.isNextStep}
      targetHandles={[{ id: 'input', position: 'top' }]} sourceHandles={[{ id: 'output', position: 'bottom' }]} />
  );
}

// ── Switch Node — N-way branching by value ───────────────────────────────
// Enhanced: "SWITCH" banner, right-side handles for each case

export function SwitchNode({ data, selected }: NodeProps) {
  const d = data as unknown as WorkflowNodeData;
  const cases = (d.config?.cases as string[]) ?? [];
  const hasCases = cases.length > 0;

  return (
    <div className={cn(
      'relative min-w-[200px] max-w-[220px] rounded-lg border-2 border-green-500/50 bg-background shadow-sm',
      selected && 'ring-2 ring-primary ring-offset-1 ring-offset-background',
    )}>
      {/* Target handle */}
      <Handle id="input" type="target" position={Position.Top}
        className="!w-2.5 !h-2.5 !bg-zinc-400 dark:!bg-zinc-500 !border-2 !border-background" />

      {/* SWITCH banner */}
      <div className="bg-green-500 text-white text-[9px] font-bold uppercase tracking-wider text-center py-0.5 rounded-t-[5px]">
        SWITCH
      </div>

      {/* Body */}
      <div className="flex items-center gap-2.5 px-3 py-2">
        <div className="flex-shrink-0 flex items-center justify-center w-7 h-7 rounded-md bg-green-500/10">
          <svg className="h-3.5 w-3.5 text-green-600 dark:text-green-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="2" /><path d="M16.24 7.76a6 6 0 0 1 0 8.49" /><path d="M19.07 4.93a10 10 0 0 1 0 14.14" /><path d="M7.76 16.24a6 6 0 0 1 0-8.49" /><path d="M4.93 19.07a10 10 0 0 1 0-14.14" /></svg>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-semibold leading-tight truncate">{d.label}</span>
            <span className={cn(
              'flex-shrink-0 w-1.5 h-1.5 rounded-full',
              d.status === 'running' ? 'bg-blue-500 animate-pulse'
                : d.status === 'success' ? 'bg-emerald-500'
                : d.status === 'error' ? 'bg-red-500'
                : 'bg-zinc-400 dark:bg-zinc-500',
            )} />
          </div>
          <p className="text-[10px] text-muted-foreground leading-tight mt-0.5 truncate">
            {hasCases ? `${cases.length} paths` : 'N-way branch'}
          </p>
        </div>
      </div>

      {/* Source handles */}
      {hasCases ? cases.map((c, i) => {
        const offset = ((i + 1) / (cases.length + 1)) * 100;
        return (
          <Handle key={`case-${i}`} id={`case-${i}`} type="source" position={Position.Right}
            style={{ top: `${offset}%` }}
            className="!w-2.5 !h-2.5 !bg-green-400 !border-2 !border-background"
          >
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[8px] text-muted-foreground whitespace-nowrap pointer-events-none">{c}</span>
          </Handle>
        );
      }) : (
        <Handle id="default" type="source" position={Position.Bottom}
          className="!w-2.5 !h-2.5 !bg-zinc-400 dark:!bg-zinc-500 !border-2 !border-background" />
      )}
    </div>
  );
}

// ── Filter Node ──────────────────────────────────────────────────────────
export function FilterNode({ data, selected }: NodeProps) {
  const d = data as unknown as WorkflowNodeData;
  const field = (d.config?.field as string) ?? '';
  return <NodeShell type="filter" label={d.label} subtitle={field ? `Keep: ${field}` : 'Filter items'} status={d.status} selected={selected} inspectData={d.inspectData} inspectStaggerIndex={d.inspectStaggerIndex} isCurrentStep={d.isCurrentStep} isNextStep={d.isNextStep} targetHandles={[{ id: 'input', position: 'top' }]} sourceHandles={[{ id: 'output', position: 'bottom' }]} />;
}

// ── Aggregate Node ───────────────────────────────────────────────────────
export function AggregateNode({ data, selected }: NodeProps) {
  const d = data as unknown as WorkflowNodeData;
  const op = (d.config?.operation as string) ?? 'count';
  return <NodeShell type="aggregate" label={d.label} subtitle={`Op: ${op}`} status={d.status} selected={selected} inspectData={d.inspectData} inspectStaggerIndex={d.inspectStaggerIndex} isCurrentStep={d.isCurrentStep} isNextStep={d.isNextStep} targetHandles={[{ id: 'input', position: 'top' }]} sourceHandles={[{ id: 'output', position: 'bottom' }]} />;
}

// ── Split Out Node ───────────────────────────────────────────────────────
export function SplitOutNode({ data, selected }: NodeProps) {
  const d = data as unknown as WorkflowNodeData;
  const field = (d.config?.arrayField as string) ?? '';
  return <NodeShell type="split_out" label={d.label} subtitle={field ? `Split: ${field}` : 'Split array → items'} status={d.status} selected={selected} inspectData={d.inspectData} inspectStaggerIndex={d.inspectStaggerIndex} isCurrentStep={d.isCurrentStep} isNextStep={d.isNextStep} targetHandles={[{ id: 'input', position: 'top' }]} sourceHandles={[{ id: 'output', position: 'bottom' }]} />;
}

// ── Sort Node ────────────────────────────────────────────────────────────
export function SortNode({ data, selected }: NodeProps) {
  const d = data as unknown as WorkflowNodeData;
  const field = (d.config?.sortField as string) ?? '';
  const dir = (d.config?.direction as string) ?? 'asc';
  return <NodeShell type="sort" label={d.label} subtitle={field ? `Sort: ${field} ${dir}` : 'Sort items'} status={d.status} selected={selected} inspectData={d.inspectData} inspectStaggerIndex={d.inspectStaggerIndex} isCurrentStep={d.isCurrentStep} isNextStep={d.isNextStep} targetHandles={[{ id: 'input', position: 'top' }]} sourceHandles={[{ id: 'output', position: 'bottom' }]} />;
}

// ── Limit Node ───────────────────────────────────────────────────────────
export function LimitNode({ data, selected }: NodeProps) {
  const d = data as unknown as WorkflowNodeData;
  const n = (d.config?.count as number) ?? 10;
  return <NodeShell type="limit" label={d.label} subtitle={`Take first ${n}`} status={d.status} selected={selected} inspectData={d.inspectData} inspectStaggerIndex={d.inspectStaggerIndex} isCurrentStep={d.isCurrentStep} isNextStep={d.isNextStep} targetHandles={[{ id: 'input', position: 'top' }]} sourceHandles={[{ id: 'output', position: 'bottom' }]} />;
}

// ── Remove Duplicates Node ───────────────────────────────────────────────
export function RemoveDuplicatesNode({ data, selected }: NodeProps) {
  const d = data as unknown as WorkflowNodeData;
  const field = (d.config?.dedupeField as string) ?? '';
  return <NodeShell type="remove_duplicates" label={d.label} subtitle={field ? `Dedupe: ${field}` : 'Remove duplicates'} status={d.status} selected={selected} inspectData={d.inspectData} inspectStaggerIndex={d.inspectStaggerIndex} isCurrentStep={d.isCurrentStep} isNextStep={d.isNextStep} targetHandles={[{ id: 'input', position: 'top' }]} sourceHandles={[{ id: 'output', position: 'bottom' }]} />;
}

// ── Error Handler Node ───────────────────────────────────────────────────
export function ErrorHandlerNode({ data, selected }: NodeProps) {
  const d = data as unknown as WorkflowNodeData;
  return <NodeShell type="error_handler" label={d.label} subtitle="Catch errors from workflow" status={d.status} selected={selected} inspectData={d.inspectData} inspectStaggerIndex={d.inspectStaggerIndex} isCurrentStep={d.isCurrentStep} isNextStep={d.isNextStep} targetHandles={[{ id: 'error-input', position: 'top' }]} sourceHandles={[{ id: 'output', position: 'bottom' }]} />;
}

// ── Stop Node ────────────────────────────────────────────────────────────
export function StopNode({ data, selected }: NodeProps) {
  const d = data as unknown as WorkflowNodeData;
  const mode = (d.config?.mode as string) ?? 'success';
  const isError = mode === 'error';

  return (
    <div className={cn(
      'relative w-[140px] rounded-full bg-background shadow-sm border-2',
      isError ? 'border-red-500/60' : 'border-zinc-400/60',
      selected && 'ring-2 ring-primary ring-offset-1 ring-offset-background',
    )}>
      {/* Target handle */}
      <Handle id="input" type="target" position={Position.Top}
        className="!w-2.5 !h-2.5 !bg-zinc-400 dark:!bg-zinc-500 !border-2 !border-background" />

      {/* Body - compact */}
      <div className="flex items-center justify-center gap-2 px-3 py-2">
        <svg className={cn('h-3.5 w-3.5 shrink-0', isError ? 'text-red-500' : 'text-zinc-500')} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" /><line x1="9" y1="9" x2="15" y2="15" /><line x1="15" y1="9" x2="9" y2="15" /></svg>
        <div className="min-w-0">
          <span className="text-[10px] font-semibold leading-tight truncate block">{d.label}</span>
          <span className={cn('text-[8px]', isError ? 'text-red-500' : 'text-muted-foreground')}>
            {isError ? 'Error' : 'End'}
          </span>
        </div>
      </div>
    </div>
  );
}

// ── Wait for Event Node ──────────────────────────────────────────────────
export function WaitForEventNode({ data, selected }: NodeProps) {
  const d = data as unknown as WorkflowNodeData;
  const eventType = (d.config?.eventType as string) ?? 'webhook';
  return <NodeShell type="wait_for_event" label={d.label} subtitle={`Wait: ${eventType}`} status={d.status} selected={selected} inspectData={d.inspectData} inspectStaggerIndex={d.inspectStaggerIndex} isCurrentStep={d.isCurrentStep} isNextStep={d.isNextStep} targetHandles={[{ id: 'input', position: 'top' }]} sourceHandles={[{ id: 'output', position: 'bottom' }]} />;
}

// ── AUTO-3 net-new node kinds ───────────────────────────────────────────
// Transform — per-item template mapping over an upstream array.
// Subtitle counts template keys so canvas authors can see at a glance
// how wide the projection is.
export function TransformNode({ data, selected }: NodeProps) {
  const d = data as unknown as WorkflowNodeData;
  const template = (d.config?.template as Record<string, unknown> | undefined) ?? {};
  const keyCount = Object.keys(template).length;
  const subtitle = keyCount > 0 ? `Map → ${keyCount} field${keyCount === 1 ? '' : 's'}` : 'Per-item mapping';
  return (
    <NodeShell
      type="transform"
      label={d.label}
      subtitle={subtitle}
      status={d.status}
      selected={selected}
      inspectData={d.inspectData}
      inspectStaggerIndex={d.inspectStaggerIndex}
      isCurrentStep={d.isCurrentStep}
      isNextStep={d.isNextStep}
      targetHandles={[{ id: 'input', position: 'top' }]}
      sourceHandles={[{ id: 'output', position: 'bottom' }]}
    />
  );
}

// Wait for Condition — predicate polling until satisfied or timeout.
// Subtitle shows the field+operator so the gate condition is visible
// without opening the property panel.
export function WaitForConditionNode({ data, selected }: NodeProps) {
  const d = data as unknown as WorkflowNodeData;
  const cfg = d.config ?? {};
  const field = (cfg.field as string) ?? '';
  const operator = (cfg.operator as string) ?? '';
  const value = (cfg.value as string) ?? '';
  const opLabel: Record<string, string> = {
    eq: '=', gt: '>', lt: '<', gte: '>=', lte: '<=',
    contains: 'contains', exists: 'exists',
  };
  const subtitle = field && operator
    ? `Until ${field} ${opLabel[operator] ?? operator} ${value}`.trim()
    : 'Poll until satisfied';
  return (
    <NodeShell
      type="wait_for_condition"
      label={d.label}
      subtitle={subtitle}
      status={d.status}
      selected={selected}
      inspectData={d.inspectData}
      inspectStaggerIndex={d.inspectStaggerIndex}
      isCurrentStep={d.isCurrentStep}
      isNextStep={d.isNextStep}
      targetHandles={[{ id: 'input', position: 'top' }]}
      sourceHandles={[{ id: 'output', position: 'bottom' }]}
    />
  );
}

// ── nodeTypes map (MUST be stable — defined at module level) ─────────────
//
// Five UI-only kinds (http_response, date_time, template, parse_generate,
// workspace_handoff) were removed in fix/automate-carryover-majors
// (round-7 NEW-MAJOR-3). They had no backend support, so any workflow
// using them could not save honestly. Re-add on both sides together
// (this file + packages/api/src/db/schema/workflow-definitions.ts
// WORKFLOW_NODE_KINDS) if shipping as real product features.

export function EmitEventNode({ data, selected }: NodeProps) {
  const d = data as unknown as WorkflowNodeData;
  const eventType = (d.config?.eventType as string) ?? 'workflow.event';
  return (
    <NodeShell
      type="emit_event"
      label={d.label}
      subtitle={`Emit ${eventType}`}
      status={d.status}
      selected={selected}
      inspectData={d.inspectData}
      inspectStaggerIndex={d.inspectStaggerIndex}
      isCurrentStep={d.isCurrentStep}
      isNextStep={d.isNextStep}
      targetHandles={[{ id: 'input', position: 'top' }]}
      sourceHandles={[{ id: 'output', position: 'bottom' }]}
    />
  );
}

export const NODE_TYPES: Record<NodeType, React.ComponentType<NodeProps>> = {
  trigger: TriggerNode,
  agent: AgentNode,
  mcp_tool: McpToolNode,
  condition: ConditionNode,
  ai_router: AiRouterNode,
  approval: ApprovalNode,
  loop: LoopNode,
  delay: DelayNode,
  webhook: WebhookNode,
  notification: NotificationNode,
  llm_generate: LlmGenerateNode,
  rag_search: RagSearchNode,
  set_variable: SetVariableNode,
  merge: MergeNode,
  code: CodeNode,
  sub_workflow: SubWorkflowNode,
  switch: SwitchNode,
  filter: FilterNode,
  aggregate: AggregateNode,
  split_out: SplitOutNode,
  sort: SortNode,
  limit: LimitNode,
  remove_duplicates: RemoveDuplicatesNode,
  error_handler: ErrorHandlerNode,
  stop: StopNode,
  wait_for_event: WaitForEventNode,
  // AUTO-3 net-new node kinds
  transform: TransformNode,
  wait_for_condition: WaitForConditionNode,
  emit_event: EmitEventNode,
};
